import { Link } from 'react-router-dom';
import type { EventItem } from '@/mocks/events';

interface EventCardProps {
  event: EventItem;
  layout?: 'vertical' | 'horizontal';
}

export default function EventCard({ event, layout = 'vertical' }: EventCardProps) {
  if (layout === 'horizontal') {
    return (
      <article className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 sm:flex-row md:p-5">
        <Link
          to={`/events/${event.id}`}
          className="relative h-44 w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-36 sm:w-52"
          tabIndex={-1}
          aria-hidden="true"
        >
          <img
            src={event.cover}
            alt={event.title}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
          />
        </Link>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-full bg-accent-100 px-2.5 py-1 font-medium text-accent-900">
              {event.category}
            </span>
            <span className="text-foreground-400">{event.campus}</span>
          </div>
          <h3 className="mt-2.5 font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
            <Link to={`/events/${event.id}`} className="transition-colors hover:text-primary-600">
              {event.title}
            </Link>
          </h3>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{event.summary}</p>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-sm leading-none text-primary-500" aria-hidden="true"></i>
              {event.heat.toLocaleString()} 热度
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line text-sm leading-none" aria-hidden="true"></i>
              {event.comments} 讨论
            </span>
            <span>{event.publishedAt}</span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link
        to={`/events/${event.id}`}
        className="relative h-48 w-full overflow-hidden bg-background-200"
        tabIndex={-1}
        aria-hidden="true"
      >
        <img
          src={event.cover}
          alt={event.title}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
        />
        <span className="absolute left-3 top-3 rounded-full bg-background-50/95 px-2.5 py-1 text-xs font-medium text-primary-700">
          {event.category}
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
          <Link to={`/events/${event.id}`} className="transition-colors hover:text-primary-600">
            {event.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">{event.summary}</p>
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-background-200 pt-3 text-xs text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-fire-line text-sm leading-none text-primary-500" aria-hidden="true"></i>
            {event.heat.toLocaleString()}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-chat-3-line text-sm leading-none" aria-hidden="true"></i>
            {event.comments}
          </span>
          <span>{event.publishedAt}</span>
        </div>
      </div>
    </article>
  );
}